(() => {
  'use strict';
  if (customElements.get('cs-browser-privacy')) return;

  const TIMEOUT = 12000;
  const available = (api) => api &&
    ['setTrackingConsent', 'currentVisitorConsent', 'saleOfDataAllowed']
      .every((name) => typeof api[name] === 'function');

  // Use Shopify's supported loader. Never read/write consent cookies directly.
  function loadPrivacy() {
    return new Promise((resolve, reject) => {
      const shopify = window.Shopify;
      if (available(shopify?.customerPrivacy)) return resolve(shopify.customerPrivacy);
      if (typeof shopify?.loadFeatures !== 'function') return reject(new Error('API unavailable'));
      const timer = setTimeout(() => reject(new Error('API load timeout')), TIMEOUT);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          clearTimeout(timer);
          if (error || !available(shopify.customerPrivacy)) reject(new Error('API load failed'));
          else resolve(shopify.customerPrivacy);
        });
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    });
  }

  function confirmed(api) {
    // An Allowed=false alone can reflect regional settings, not a saved choice.
    return api.currentVisitorConsent().sale_of_data === 'no' && api.saleOfDataAllowed() === false;
  }

  function saveOptOut(api) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Save timeout')), TIMEOUT);
      try {
        // Deliberately leave analytics, marketing and preferences untouched.
        // This is browser-scoped, not Shopify's customer/account mutation.
        api.setTrackingConsent({ sale_of_data: false }, () => {
          clearTimeout(timer);
          try {
            if (!confirmed(api)) throw new Error('Opt-out not confirmed');
            resolve();
          } catch (error) { reject(error); }
        });
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    });
  }

  class BrowserPrivacy extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.revision = (this.revision || 0) + 1;
      this.button = this.querySelector('[data-privacy-submit]');
      this.status = this.querySelector('[data-privacy-status]');
      if (!this.button || !this.status) return;
      this.onClick = () => this.optOut();
      this.onConsent = () => { if (!this.busy) this.refresh(); };
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('visitorConsentCollected', this.onConsent);
      window.addEventListener('pageshow', this.onConsent);
      this.button.disabled = false;
      this.refresh(); // Read only; never grant or decline consent on page load.
    }

    disconnectedCallback() {
      this.connected = false;
      this.revision += 1;
      this.busy = false;
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      window.removeEventListener('pageshow', this.onConsent);
    }

    setBusy(value) {
      this.busy = value;
      // Keep keyboard focus on the button, including during save and errors.
      this.button.setAttribute('aria-disabled', String(value));
    }

    show(state) {
      this.dataset.state = state;
      this.status.textContent = this.dataset[state];
    }

    async refresh() {
      if (this.busy) return;
      const revision = ++this.revision;
      this.setBusy(true);
      try {
        const api = await loadPrivacy();
        if (this.connected && revision === this.revision) this.show(confirmed(api) ? 'saved' : 'ready');
      } catch (error) {
        if (this.connected && revision === this.revision) this.show('error');
      } finally {
        if (this.connected && revision === this.revision) this.setBusy(false);
      }
    }

    async optOut() {
      if (this.busy) return;
      const revision = ++this.revision;
      this.setBusy(true);
      this.show('saving');
      try {
        const api = await loadPrivacy();
        if (!this.connected || revision !== this.revision) return;
        await saveOptOut(api);
        if (this.connected && revision === this.revision) this.show('saved');
      } catch (error) {
        if (this.connected && revision === this.revision) this.show('error');
      } finally {
        if (this.connected && revision === this.revision) this.setBusy(false);
      }
    }
  }

  customElements.define('cs-browser-privacy', BrowserPrivacy);
})();
